import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn } from "~/../lib/utils";

type StepperContextType = {
  value: number;
  setValue: (step: number) => void;
  orientation?: "horizontal" | "vertical";
  steps: number[];
  registerStep: (step: number) => void;
};

const StepperContext = React.createContext<StepperContextType | null>(null);

function useStepper() {
  const ctx = React.useContext(StepperContext);
  if (!ctx) throw new Error("Stepper components must be used inside Stepper");
  return ctx;
}

export function Stepper({
  value,
  onValueChange,
  orientation = "horizontal",
  children,
  className,
}: {
  value: number;
  onValueChange: (step: number) => void;
  orientation?: "horizontal" | "vertical";
  children: React.ReactNode;
  className?: string;
}) {
  const [steps, setSteps] = React.useState<number[]>([]);

  const registerStep = React.useCallback((step: number) => {
    setSteps((prev) =>
      prev.includes(step) ? prev : [...prev, step].sort((a, b) => a - b),
    );
  }, []);

  return (
    <StepperContext.Provider
      value={{
        value,
        setValue: onValueChange,
        orientation,
        steps,
        registerStep,
      }}
    >
      <div
        role="tablist"
        aria-orientation={orientation}
        className={cn(
          "flex",
          orientation === "horizontal"
            ? "flex-row items-start gap-4"
            : "flex-col gap-6",
          className,
        )}
      >
        {children}
      </div>
    </StepperContext.Provider>
  );
}

export function StepperItem({
  step,
  children,
  className,
}: {
  step: number;
  children: React.ReactNode | ((props: { state: string }) => React.ReactNode);
  className?: string;
}) {
  const { value, registerStep } = useStepper();

  React.useEffect(() => {
    registerStep(step);
  }, [step, registerStep]);

  let state: "inactive" | "active" | "completed" = "inactive";
  if (value === step) state = "active";
  if (value > step) state = "completed";

  return (
    <div
      data-state={state}
      className={cn("group flex flex-col items-center", className)}
    >
      {typeof children === "function" ? children({ state }) : children}
    </div>
  );
}

export function StepperTrigger({
  step,
  asChild = false,
  className,
  children,
}: {
  step: number;
  asChild?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const Comp = asChild ? Slot : "button";
  const { value, setValue } = useStepper();
  const isActive = value === step;

  return (
    <Comp
      role="tab"
      aria-selected={isActive}
      aria-controls={`step-panel-${step}`}
      id={`step-trigger-${step}`}
      tabIndex={isActive ? 0 : -1}
      onClick={() => setValue(step)}
      className={cn("outline-none", className)}
    >
      {children}
    </Comp>
  );
}

export function StepperIndicator({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex h-10 w-10 items-center justify-center rounded-full border text-sm transition-colors",
        "bg-muted text-muted-foreground",
        "group-data-[state=active]:bg-primary group-data-[state=active]:text-primary-foreground",
        "group-data-[state=completed]:bg-primary group-data-[state=completed]:text-primary-foreground",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function StepperSeparator({ className }: { className?: string }) {
  const { orientation } = useStepper();

  return (
    <div
      aria-hidden
      className={cn(
        "bg-muted",
        orientation === "horizontal" ? "h-0.5 flex-1" : "w-0.5 h-8 ml-5",
        "group-data-[state=completed]:bg-primary",
        className,
      )}
    />
  );
}

export function StepperContent({
  step,
  children,
  className,
}: {
  step: number;
  children: React.ReactNode;
  className?: string;
}) {
  const { value } = useStepper();
  const isActive = value === step;

  return (
    <div
      role="tabpanel"
      id={`step-panel-${step}`}
      aria-labelledby={`step-trigger-${step}`}
      hidden={!isActive}
      className={cn("mt-4", className)}
    >
      {isActive && children}
    </div>
  );
}

export const StepperTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("text-sm font-medium", className)} {...props} />
);

export const StepperDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("text-xs text-muted-foreground", className)} {...props} />
);
